import { getRefundInfo } from '../RefundPill/refundInfo'
import formatAmount from '../../utils/formatMoney'

const money = (amount, currency) => formatAmount(amount, currency) ?? '-'

const PROCESSING_MESSAGE =
  'Mercado Pago todavía no confirma la devolución. Queda en proceso y llegó un aviso a pagos: en unos minutos volvé a tocar «Reembolsar restante», que revisa Mercado Pago antes de pedir nada.'

const RESOLVED_MESSAGES = {
  synced: 'Mercado Pago ya lo había devuelto. Se cerró la transacción.',
  refunded: 'Se devolvió el restante y se cerró la transacción.'
}

const PARTIAL_UNCHANGED = 'El reembolso parcial sigue como estaba.'

const REVIEW_MESSAGES = {
  amount_mismatch: `Mercado Pago muestra una devolución por otro monto. Revisalo en Mercado Pago. ${PARTIAL_UNCHANGED}`,
  amount_unknown: `No se sabe cuánto se devolvió en el reembolso parcial. Revisalo en Mercado Pago. ${PARTIAL_UNCHANGED}`,
  unexpected_provider_status: `Mercado Pago muestra el pago en un estado inesperado, por ejemplo un contracargo. Revisalo en Mercado Pago. ${PARTIAL_UNCHANGED}`,
  provider_unavailable: 'No se pudo consultar Mercado Pago. Probá de nuevo en un rato.',
  seat_unknown: 'No se pudo revisar si el pasajero está en un viaje. Probá de nuevo en un rato.',
  driver_unknown: 'No se pudo revisar el pago del conductor. Probá de nuevo en un rato.',
  missing_payment: `La transacción no tiene un pago de Mercado Pago. ${PARTIAL_UNCHANGED}`
}

export const DRIVER_NOTES = {
  retained: amount => `Hay ${amount} retenidos para el conductor en este viaje, que todavía no terminó.`,
  pending_payment: amount => `El conductor tiene ${amount} de este pasajero en un pago que todavía no salió.`,
  paid: amount => `El conductor ya cobró ${amount} de este pasajero: el restante lo pone Viatik.`,
  none: () => 'No hay plata del conductor en juego.'
}

export const isRemainderRefundable = (info, status) =>
  status === 'approved' && info?.partial === true && info.state === 'completed'

export const driverNote = (driver, currency) => {
  const note = DRIVER_NOTES[driver?.state]
  return note ? note(money(driver.amount, currency)) : null
}

export const destinationNote = ({ provider_amount: provider, wallet_amount: wallet, currency }) => {
  if (provider > 0 && wallet > 0) {
    return `${money(provider, currency)} vuelven al medio de pago y ${money(wallet, currency)} al saldo Viatik.`
  }
  if (wallet > 0) return 'Vuelve al saldo Viatik del pasajero.'
  return 'Vuelve al medio de pago original. Si ya lo devolviste desde el panel de Mercado Pago, sólo se cierra la transacción.'
}

export const remainderProblemMessage = (code, detail = {}) => {
  switch (code) {
    case 'nothing_left':
      return 'Ya se devolvió todo lo que se cobró: no queda nada por devolver.'
    case 'already_refunded':
      return 'Esta transacción ya está reembolsada.'
    case 'not_partial':
      return 'Esta transacción no tiene un reembolso parcial.'
    case 'not_refundable':
      return 'La transacción ya no figura cobrada. Recargá para ver cómo quedó.'
    case 'refund_in_progress':
      return 'Hay un reembolso en curso para esta transacción. Esperá a que termine.'
    case 'alternative_payment':
      return 'Es un pago por transferencia o depósito: el restante se devuelve a mano.'
    case 'double_charge':
      return 'La transacción tiene otro cobro. Revisalo en Mercado Pago y devolvelo desde ahí.'
    case 'amount_mismatch':
      return 'Lo cobrado no coincide con el precio del viaje. Revisalo en Mercado Pago y devolvelo desde ahí.'
    case 'amount_unknown':
      return 'No se sabe cuánto se devolvió en el reembolso parcial. Revisalo en Mercado Pago.'
    case 'invalid_currency':
      return 'La transacción no tiene una moneda válida.'
    case 'companion_in_lobby':
      return 'El pasajero está en un viaje con este pago, así que no se devolvió. Revisá si todavía corresponde.'
    case 'driver_share_required':
    case 'invalid_driver_share':
      return 'Elegí quién pone la plata del conductor.'
    case 'driver_share_changed':
      return 'El conductor cobró mientras tanto: ya no se le puede descontar. Si lo devolvés igual, lo pone Viatik.'
    case 'already_refunding':
      return 'Otro proceso empezó a devolverlo. Recargá para ver cómo quedó.'
    case 'refunds_paused':
      return 'Los reembolsos automáticos están en pausa. Resolvelo a mano.'
    case 'insufficient_funds':
      return `La cuenta de Mercado Pago no tiene saldo disponible para devolverlo. ${PARTIAL_UNCHANGED} Probá de nuevo cuando haya saldo.`
    case 'refund_failed':
      return detail.message
        ? `Mercado Pago rechazó la devolución: ${detail.message}. ${PARTIAL_UNCHANGED}`
        : `Mercado Pago rechazó la devolución. ${PARTIAL_UNCHANGED}`
    case 'needs_review':
      return REVIEW_MESSAGES[detail.reason] || `No se pudo devolver solo. Revisalo en Mercado Pago. ${PARTIAL_UNCHANGED}`
    case 'not_found':
      return 'No existe una transacción con ese ID.'
    default:
      return 'No se pudo devolver el restante. Probá de nuevo en un rato.'
  }
}

export const problemOf = ({ status, data }) => {
  const code = data?.code || (status === 404 ? 'not_found' : null)
  return remainderProblemMessage(code, data || {})
}

const successOutcome = (data, { driverShare, currency }) => {
  const deducted = data?.driver_deducted
  const base = RESOLVED_MESSAGES[data?.result] || RESOLVED_MESSAGES.refunded
  const update = { status: data?.status, refund: data?.refund }
  if (deducted > 0) {
    return {
      done: true,
      severity: 'success',
      message: `${base} Se le descontaron ${money(deducted, currency)} al conductor.`,
      update
    }
  }
  if (driverShare === 'deduct') {
    return {
      done: true,
      severity: 'warning',
      message: `${base} No se pudo descontar al conductor: llegó un aviso a pagos para ajustarlo a mano.`,
      update
    }
  }
  return { done: true, severity: 'success', message: base, update }
}

export const remainderOutcome = ({ ok, status, data }, context = {}) => {
  if (status === 202) return { done: true, severity: 'info', message: PROCESSING_MESSAGE, update: {} }
  if (ok) return successOutcome(data, context)
  return { done: false, severity: 'error', message: problemOf({ status, data }), driver: data?.driver ?? null }
}

export const refundAfterRemainder = (update, transaction) => {
  if (update?.refund) return update.refund
  const remainder = { state: 'processing' }
  if (transaction?.refund && typeof transaction.refund === 'object') return { ...transaction.refund, remainder }
  const info = getRefundInfo(transaction)
  return {
    method: info?.method,
    state: info?.state,
    partial: info?.partial,
    amount: info?.amount ?? null,
    currency: info?.currency ?? null,
    refund_reason: info?.reason ?? null,
    remainder
  }
}
